import React, { createContext, useContext, useEffect, useState } from "react";

export type ThemeKey = "obsidian" | "graphite" | "emerald";

export interface ThemeMeta {
  key: ThemeKey;
  name: string;
  tagline: string;
  swatch: string[]; // hex previews
}

export const THEMES: ThemeMeta[] = [
  {
    key: "obsidian",
    name: "Obsidian Luxe",
    tagline: "High-contrast luxury — matches the Coornachem reference",
    swatch: ["#0B0B0F", "#2A2C2F", "#D4AF37", "#00C896", "#F4F5F7"],
  },
  {
    key: "graphite",
    name: "Graphite Pro",
    tagline: "Warmer, architectural — finance / portfolio mood",
    swatch: ["#14161A", "#2E3035", "#D4AF37", "#00C896", "#F4F5F7"],
  },
  {
    key: "emerald",
    name: "Midnight Emerald",
    tagline: "AI-forward — Tesla-style intelligence emphasis",
    swatch: ["#090B0E", "#1E2428", "#D4AF37", "#10DCA8", "#F4FAF8"],
  },
];

interface Ctx {
  theme: ThemeKey;
  setTheme: (t: ThemeKey) => void;
}

const ThemeCtx = createContext<Ctx>({ theme: "obsidian", setTheme: () => {} });

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeKey>("obsidian");

  useEffect(() => {
    const stored = (typeof window !== "undefined" && localStorage.getItem("envella-theme")) as ThemeKey | null;
    if (stored && THEMES.find((t) => t.key === stored)) {
      setThemeState(stored);
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const setTheme = (t: ThemeKey) => {
    setThemeState(t);
    if (typeof window !== "undefined") localStorage.setItem("envella-theme", t);
  };

  return <ThemeCtx.Provider value={{ theme, setTheme }}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
  return useContext(ThemeCtx);
}
